import { protectedRpc } from './rpc.ts'

/**
 * The fields of `interface.list` this app reads. The full object is wider
 * (uuid, time_zone, cfg_*, …) and is passed back untouched by `interface.update`,
 * which this app never calls. Nullable exactly where the cloud is nullable.
 */
export interface ControllerInterface {
  id: number
  /** Display name — the cloud leaves it null on a controller nobody has named. */
  description: string | null
  organization_id: number
  product_id?: string | null
  data?: { hostname?: string } | null
  /** 1 = Online, 2 = Warning, 3 = Error, null = unknown. */
  state?: number | null
  time_zone?: string | null
  /** Without a log subscription there is no log_data to report on. */
  log_subscribed?: boolean
}

export interface Organization {
  id: number
  name: string
  parent_id?: number
  description?: string | null
}

interface ListReply<T> {
  items?: T[]
  total?: number
}

/**
 * The authorisation boundary, and the only one needed: the cloud returns
 * exactly the interfaces this user may see. Never filter by organization_id
 * yourself, and never accept a controller id that isn't in this list.
 */
export async function listInterfaces(): Promise<ControllerInterface[]> {
  const r = await protectedRpc<ListReply<ControllerInterface>>('interface.list', {})
  return (r?.items ?? []).map((i) => ({ ...i, id: Number(i.id) }))
}

/** interface.list carries organization_id but not the name; this is the lookup. */
export async function listOrganizations(): Promise<Organization[]> {
  const r = await protectedRpc<ListReply<Organization>>('organization.organization_list', {})
  return (r?.items ?? []).map((o) => ({ ...o, id: Number(o.id) }))
}

export function stateLabel(
  state: number | null | undefined,
): { text: string; tone: 'good' | 'warning' | 'critical' | 'unknown' } {
  if (state === 1) return { text: 'Online', tone: 'good' }
  if (state === 2) return { text: 'Warning', tone: 'warning' }
  if (state === 3) return { text: 'Error', tone: 'critical' }
  return { text: 'Unknown', tone: 'unknown' }
}

/** What to call a controller: its name, else its hostname, else its id. */
export function controllerName(i: ControllerInterface): string {
  return i.description?.trim() || i.data?.hostname?.trim() || `Controller ${i.id}`
}

/** Named controllers alphabetically, unnamed ones after them by id. */
export function sortControllers(list: ControllerInterface[]): ControllerInterface[] {
  return list.slice().sort((a, b) => {
    const an = a.description?.trim() ?? ''
    const bn = b.description?.trim() ?? ''
    if (an && bn) return an.localeCompare(bn)
    if (an) return -1
    if (bn) return 1
    return a.id - b.id
  })
}

/**
 * Does a controller match a search? Case-insensitive, every whitespace-
 * separated term must hit somewhere in name, hostname, product, id, or the
 * organisation's name.
 */
export function matchesSearch(i: ControllerInterface, orgName: string | undefined, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return true
  const hay = [
    i.description ?? '',
    i.data?.hostname ?? '',
    i.product_id ?? '',
    String(i.id),
    orgName ?? '',
  ]
    .join(' ')
    .toLowerCase()
  return terms.every((t) => hay.includes(t))
}
